import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { storage } from '../../data/localStorage';
import ItemSelectionFilter from './ItemSelectionFilter';
import AnalysisRunSummary from './AnalysisRunSummary';
import MetadataAnalysis from './MetadataAnalysis';
import TextComplexityAnalysis from './TextComplexityAnalysis';
import KeywordAnalysis from './KeywordAnalysis';
import QuestionTypeClassifier from './QuestionTypeClassifier';
import DistractorAnalysis from './DistractorAnalysis';
import CognitiveLevelAnalysis from './CognitiveLevelAnalysis';
import FormattingChecks from './FormattingChecks';
import LexicalDiversityAnalysis from './LexicalDiversityAnalysis';
import AnswerLengthPrediction from './AnswerLengthPrediction';
import DuplicateDetection from './DuplicateDetection';
import RaschAnalysis from './RaschAnalysis';
import SyntaxComplexityAnalysis from './SyntaxComplexityAnalysis';
import ItemAnalysisGlossary from './ItemAnalysisGlossary';

const { 
  FiBarChart2, FiFilter, FiDownload, FiPlayCircle, FiCheckCircle, 
  FiPlusCircle, FiMinusCircle, FiBookOpen, FiInfo, FiPrinter,
  FiAlertCircle, FiAlertTriangle, FiRefreshCw, FiTrendingUp,
  FiActivity, FiTarget, FiLayers
} = FiIcons;

const ItemAnalysis = () => {
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeAnalyses, setActiveAnalyses] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [showGlossary, setShowGlossary] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isAnalysisRunning, setIsAnalysisRunning] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [showSelectionUI, setShowSelectionUI] = useState(true);

  // All available analysis methods with gradient backgrounds
  const analysisTypes = [
    { 
      id: 'rasch', 
      name: 'Rasch-friendly Analysis', 
      icon: FiTarget,
      gradient: 'from-purple-500 to-indigo-600',
      textColor: 'text-white'
    },
    { 
      id: 'metadata', 
      name: 'Basic Metadata Analysis', 
      icon: FiBarChart2,
      gradient: 'from-blue-400 to-blue-600',
      textColor: 'text-white'
    },
    { 
      id: 'complexity', 
      name: 'Text Complexity Analysis', 
      icon: FiBookOpen,
      gradient: 'from-green-400 to-teal-500',
      textColor: 'text-white'
    },
    { 
      id: 'syntaxComplexity', 
      name: 'Syntactic Complexity Analysis', 
      icon: FiLayers,
      gradient: 'from-purple-400 to-blue-500',
      textColor: 'text-white'
    },
    { 
      id: 'keyword', 
      name: 'Keyword & Term Frequency', 
      icon: FiFilter,
      gradient: 'from-yellow-400 to-orange-500',
      textColor: 'text-white'
    },
    { 
      id: 'questionType', 
      name: 'Question Type Classification', 
      icon: FiCheckCircle,
      gradient: 'from-pink-400 to-rose-600',
      textColor: 'text-white'
    },
    { 
      id: 'distractor', 
      name: 'Distractor Analysis (MCQs)', 
      icon: FiPlusCircle,
      gradient: 'from-red-400 to-red-600',
      textColor: 'text-white'
    },
    { 
      id: 'cognitive', 
      name: 'Cognitive Level Analysis', 
      icon: FiInfo,
      gradient: 'from-indigo-400 to-purple-500',
      textColor: 'text-white'
    },
    { 
      id: 'formatting', 
      name: 'Consistency & Formatting Checks', 
      icon: FiLayers,
      gradient: 'from-cyan-400 to-blue-500',
      textColor: 'text-white'
    },
    { 
      id: 'lexical', 
      name: 'Lexical Diversity Analysis', 
      icon: FiActivity,
      gradient: 'from-amber-400 to-orange-600',
      textColor: 'text-white'
    },
    { 
      id: 'answerLength', 
      name: 'Answer Length Prediction', 
      icon: FiTrendingUp,
      gradient: 'from-emerald-400 to-green-600',
      textColor: 'text-white'
    },
    { 
      id: 'duplicate', 
      name: 'Duplicate Detection', 
      icon: FiFilter,
      gradient: 'from-violet-400 to-indigo-600',
      textColor: 'text-white'
    }
  ];

  // Load questions from storage
  useEffect(() => {
    const loadQuestions = async () => {
      setIsLoading(true);
      try {
        const storedQuestions = storage.getQuestions();
        setQuestions(storedQuestions);
        
        // By default, select all questions
        if (storedQuestions.length > 0) {
          setSelectedQuestionIds(storedQuestions.map(q => q.id));
        }
      } catch (error) {
        console.error('Error loading questions:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadQuestions();
  }, []);

  // Get selected questions
  const selectedQuestions = questions.filter(q => selectedQuestionIds.includes(q.id));
  
  // Get selected analysis types objects
  const selectedAnalysisTypes = analysisTypes.filter(type => activeAnalyses.includes(type.id));

  // Toggle analysis type selection
  const toggleAnalysis = (id) => {
    if (activeAnalyses.includes(id)) {
      setActiveAnalyses(activeAnalyses.filter(item => item !== id));
    } else {
      setActiveAnalyses([...activeAnalyses, id]);
    }
  };

  // Select all analysis types
  const selectAllAnalyses = () => {
    setActiveAnalyses(analysisTypes.map(type => type.id));
  };

  // Clear all selected analysis types
  const clearAllAnalyses = () => {
    setActiveAnalyses([]);
  };

  // Run the selected analyses
  const runAnalyses = () => {
    if (activeAnalyses.length === 0) {
      alert('Please select at least one analysis method');
      return;
    }
    
    if (selectedQuestionIds.length === 0) {
      alert('Please select at least one question to analyze');
      return;
    }
    
    setIsAnalysisRunning(true);
    
    // Simulate analysis processing time
    setTimeout(() => {
      setIsAnalysisRunning(false);
      setAnalysisComplete(true);
      setShowSelectionUI(false);
      
      // Scroll to results
      setTimeout(() => {
        document.getElementById('analysis-report').scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }, 1500);
  };

  // Reset analysis and show selection UI again
  const resetAnalysis = () => {
    setAnalysisComplete(false);
    setShowSelectionUI(true);
  };

  // Print report using browser's print functionality
  const printReport = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 300);
  };

  // Render analysis component based on type
  const renderAnalysis = (type) => {
    switch (type) {
      case 'metadata':
        return <MetadataAnalysis questions={selectedQuestions} />;
      case 'complexity':
        return <TextComplexityAnalysis questions={selectedQuestions} />;
      case 'syntaxComplexity':
        return <SyntaxComplexityAnalysis questions={selectedQuestions} />;
      case 'keyword':
        return <KeywordAnalysis questions={selectedQuestions} />;
      case 'questionType':
        return <QuestionTypeClassifier questions={selectedQuestions} />;
      case 'distractor':
        return <DistractorAnalysis questions={selectedQuestions} />;
      case 'cognitive':
        return <CognitiveLevelAnalysis questions={selectedQuestions} />;
      case 'formatting':
        return <FormattingChecks questions={selectedQuestions} />;
      case 'lexical':
        return <LexicalDiversityAnalysis questions={selectedQuestions} />;
      case 'answerLength':
        return <AnswerLengthPrediction questions={selectedQuestions} />;
      case 'duplicate':
        return <DuplicateDetection questions={selectedQuestions} />;
      case 'rasch':
        return <RaschAnalysis questions={selectedQuestions} />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-8 ml-72">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading questions for analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8 ml-72">
      {/* Header */}
      <div className="flex justify-between items-center print:hidden">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Item Analysis</h2>
          <p className="text-gray-600 mt-1">
            Analyze your question bank with multiple metrics and insights
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <motion.button
            onClick={() => setShowGlossary(!showGlossary)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={showGlossary ? FiMinusCircle : FiPlusCircle} />
            <span>{showGlossary ? 'Hide Glossary' : 'Show Glossary'}</span>
          </motion.button>
          
          {analysisComplete && (
            <>
              <motion.button
                onClick={resetAnalysis}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-700 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiRefreshCw} />
                <span>New Analysis</span>
              </motion.button>
              
              <motion.button
                onClick={printReport}
                className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isPrinting}
              >
                <SafeIcon icon={isPrinting ? FiBarChart2 : FiPrinter} className={isPrinting ? "animate-spin" : ""} />
                <span>{isPrinting ? 'Preparing...' : 'Print Report'}</span>
              </motion.button>
            </>
          )}
        </div>
      </div>

      {/* Print Title - Only visible when printing */}
      <div className="hidden print:block print:mb-8">
        <h1 className="text-3xl font-bold text-center">Question Bank Item Analysis Report</h1>
        <p className="text-center text-gray-600 mt-2">
          Generated on {new Date().toLocaleDateString()} | Total Questions: {selectedQuestions.length}
        </p>
      </div>

      {/* Glossary */}
      {showGlossary && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="print:block"
        >
          <ItemAnalysisGlossary />
        </motion.div>
      )}

      {/* Question Selection Filter */}
      {showSelectionUI && (
        <div className="print:hidden">
          <ItemSelectionFilter 
            questions={questions} 
            selectedQuestions={selectedQuestionIds}
            setSelectedQuestions={setSelectedQuestionIds}
          />
        </div>
      )}

      {/* Analysis Selection */}
      {showSelectionUI && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 print:hidden">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
            <div className="mb-4 md:mb-0">
              <h3 className="text-lg font-semibold text-gray-800">Analysis Selection</h3>
              <p className="text-sm text-gray-600">Choose which analysis methods to run</p>
            </div>
            <div className="flex items-center space-x-3">
              <motion.button
                onClick={selectAllAnalyses}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm flex items-center space-x-1"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiCheckCircle} className="text-green-600" />
                <span>Select All</span>
              </motion.button>
              <motion.button
                onClick={clearAllAnalyses}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm flex items-center space-x-1"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiMinusCircle} className="text-red-600" />
                <span>Clear All</span>
              </motion.button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {analysisTypes.map(type => (
              <motion.button
                key={type.id}
                onClick={() => toggleAnalysis(type.id)}
                className={`p-4 rounded-lg border text-sm text-center transition-colors flex flex-col items-center justify-center h-28 overflow-hidden relative ${
                  activeAnalyses.includes(type.id)
                    ? `bg-gradient-to-br ${type.gradient} border-transparent shadow-md`
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
                whileHover={{ scale: 1.03, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Animated background for selected items */}
                {activeAnalyses.includes(type.id) && (
                  <motion.div 
                    className="absolute inset-0 opacity-20"
                    initial={{ opacity: 0 }}
                    animate={{ 
                      opacity: [0.1, 0.2, 0.1],
                      scale: [1, 1.05, 1],
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    <div className="absolute -inset-[100%] rounded-full bg-white/30 blur-3xl"></div>
                  </motion.div>
                )}
                
                <SafeIcon 
                  icon={type.icon} 
                  className={`text-2xl mb-2 ${
                    activeAnalyses.includes(type.id) ? `${type.textColor}` : 'text-gray-400'
                  }`} 
                />
                <span className={activeAnalyses.includes(type.id) ? type.textColor : ""}>
                  {type.name}
                </span>
              </motion.button>
            ))}
          </div>

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {selectedQuestionIds.length === 0 ? (
                <span className="text-orange-600 flex items-center">
                  <SafeIcon icon={FiAlertCircle} className="mr-1" />
                  No questions selected for analysis
                </span>
              ) : (
                <span>
                  {selectedQuestionIds.length} question{selectedQuestionIds.length !== 1 ? 's' : ''} selected for analysis
                </span>
              )}
            </div>
            
            <motion.button
              onClick={runAnalyses}
              className={`px-6 py-2 rounded-lg flex items-center space-x-2 ${
                activeAnalyses.length === 0 || selectedQuestionIds.length === 0 || isAnalysisRunning
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-md'
              } transition-colors`}
              whileHover={activeAnalyses.length > 0 && selectedQuestionIds.length > 0 && !isAnalysisRunning ? { scale: 1.02 } : {}}
              whileTap={activeAnalyses.length > 0 && selectedQuestionIds.length > 0 && !isAnalysisRunning ? { scale: 0.98 } : {}}
              disabled={activeAnalyses.length === 0 || selectedQuestionIds.length === 0 || isAnalysisRunning}
            >
              <SafeIcon icon={isAnalysisRunning ? FiBarChart2 : FiPlayCircle} className={isAnalysisRunning ? "animate-spin" : ""} />
              <span>{isAnalysisRunning ? 'Analyzing...' : 'Run Analysis'}</span>
            </motion.button>
          </div>
        </div>
      )}

      {/* Analysis Results */}
      <div id="analysis-report" className="space-y-8">
        {/* Show warning if no questions are selected */}
        {selectedQuestionIds.length === 0 && !isAnalysisRunning && !analysisComplete && (
          <div className="bg-orange-50 p-6 rounded-lg border border-orange-200 text-center print:hidden">
            <SafeIcon icon={FiAlertTriangle} className="mx-auto text-4xl text-orange-500 mb-4" />
            <h3 className="text-xl font-medium text-orange-800 mb-2">No Questions Selected</h3>
            <p className="text-orange-700">
              Please select at least one question to analyze from the question selection panel above.
            </p>
          </div>
        )}
        
        {/* Show message if no analysis types are selected */}
        {selectedQuestionIds.length > 0 && activeAnalyses.length === 0 && !isAnalysisRunning && !analysisComplete && (
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 text-center print:hidden">
            <SafeIcon icon={FiInfo} className="mx-auto text-4xl text-blue-500 mb-4" />
            <h3 className="text-xl font-medium text-blue-800 mb-2">Select Analysis Methods</h3>
            <p className="text-blue-700">
              Choose one or more analysis methods above to analyze your {selectedQuestionIds.length} selected questions.
            </p>
          </div>
        )}
        
        {/* Show loading indicator while analysis is running */}
        {isAnalysisRunning && (
          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 text-center">
            <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h3 className="text-xl font-medium text-gray-800 mb-2">Running Analysis</h3>
            <p className="text-gray-600">
              Analyzing {selectedQuestionIds.length} questions with {activeAnalyses.length} metrics...
            </p>
          </div>
        )}

        {/* Show results if analysis is complete */}
        {analysisComplete && (
          <>
            {/* Analysis Summary */}
            <AnalysisRunSummary 
              questions={selectedQuestions}
              analysisTypes={selectedAnalysisTypes}
            />
            
            {/* Individual Analysis Results */}
            {activeAnalyses.map(type => (
              <motion.div
                key={type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="print:mb-12 print:page-break-inside-avoid"
              >
                {renderAnalysis(type)}
              </motion.div>
            ))}
          </>
        )}
      </div>
      
      {/* Print styles */}
      <style jsx="true">{`
        @media print {
          @page {
            margin: 1cm;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          .print\\:page-break-inside-avoid {
            page-break-inside: avoid;
          }
          .print\\:mb-8 {
            margin-bottom: 2rem;
          }
          .print\\:mb-12 {
            margin-bottom: 3rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ItemAnalysis;